import type { BoxCollider, FloorRegion } from './collision';
import { PLAYER_RADIUS } from './controller';

/**
 * Wave D-3 (PLAN-CITY2 §4): the water-hole sewer den, hosting a single
 * `sewer_elite` (ai/archetypes.ts, ai/enemy.ts's 'burrower' mode — built by
 * C08/Wave M, never spawned until now). Entered via a street-level trap
 * hole placed on the block's water-facing (north) edge — Karan's own words,
 * "a trap hole near the water area" — toward the Hell Gate/RFK vista
 * (world/layout.ts's B-3 `RFK_VISTA_Z`/`RFK_TOWER_XS`, both at the block's
 * own far-north z=100). `TRAP_HOLE_POS` sits inside the real play boundary
 * (±38) at the north edge, clear of every existing habitat/collider/prop
 * (checked by hand against `ai/habitats.ts` and every `world/layout.ts`
 * collider array — nearest is the utility-tunnel corridor's own boundary
 * gap at x∈[13.6,20.4], a real 5.6m clear of this spot's x=6).
 *
 * `SEWER_ORIGIN` (x=600) sits far past every other isolated room's own
 * self-heal threshold (LAIR_ORIGIN=480 is the nearest, 120m clear).
 */
export const TRAP_HOLE_POS = { x: 6, z: 35 };
export const TRAP_HOLE_PLACEMENT_Y = -1.2; // matches the delivered prp_trap_hole.glb's own NOTES.md placementY

export const SEWER_ORIGIN = { x: 600, z: 0 };

/**
 * `blk_sewer_pipe`: 2 segments (4.0m each, per NOTES.md's outer profile
 * 2.6×2.6m / clear interior ≥2.2×2.2m — a short connector, "not a maze," per
 * the brief). Runs south-to-north (increasing Z) into the chamber's own
 * OPEN south mouth (the independent GLB inspection this session ran found
 * the chamber's south wall has a real 2.6m gap with no grate geometry —
 * `north_segment`/`north_lintel`'s matching gap is where the delivered
 * `shut_grate_h/v` nodes actually sit, per NOTES.md's "one open/entry, one
 * visibly grated shut"). `PIPE_ENTRY` is where the street trap-hole teleport
 * lands the player, facing north into the bore.
 */
const PIPE_SEGMENT_LENGTH = 4.0;
const PIPE_SEGMENT_COUNT = 2;
export const PIPE_RUN_LENGTH = PIPE_SEGMENT_LENGTH * PIPE_SEGMENT_COUNT; // 8.0m
export const PIPE_HALF_WIDTH = 1.3; // 2.6m outer profile
export const PIPE_ENTRY = { x: SEWER_ORIGIN.x, z: SEWER_ORIGIN.z - 5 - PIPE_RUN_LENGTH }; // -13
const PIPE_CLEAR_HALF = 1.1; // ≥2.2m clear interior, per NOTES.md
export const PIPE_WALK_FLOOR_Y = 0.2; // matches the delivered blk_sewer_pipe.glb's own flat_base top (local Y=0.2, placementY=0)

/**
 * Fix session (adversarial review, all 3 lenses independently confirmed
 * HIGH): the ORIGINAL south end-cap sat flush against `PIPE_ENTRY.z` (the
 * delivered `blk_sewer_pipe.glb`'s own visual south mouth) — `resolveHorizontal`
 * expands that cap by `PLAYER_RADIUS` (0.3), so the deepest z a player could
 * ever reach was -12.7, while `isPastPipeEntryThreshold` required z<-13.35 to
 * fire. The two numbers never overlapped: the walk-out trigger was
 * unreachable by any real movement, and the only way out of the den was
 * dying (the >50m self-heal) or reloading. This stub pushes the end-cap (and
 * the corridor's own side walls / floor region) an extra 1.4m further south,
 * past the pipe's own rendered mouth — invisible dressing-wise (this whole
 * room is a minimal isolated origin with no camera-visible landmark back
 * there), but it gives the exit threshold real, walkable room to fire in.
 */
const PIPE_EXIT_STUB = 1.4;

/**
 * Camera: a 2.2m-clear bore (crown at local Y=2.6, walk floor at Y=0.2 —
 * 2.4m clear height) needs its OWN tighter clamp than the chamber's — the
 * brief's own math: 2.3m default clamp would put the camera above the
 * crown in a bore this tight (compare `LAUNDROMAT_CAMERA_MAX_DISTANCE`-style
 * derivation: at pitch 0.55rad, d=2.3 puts the camera ~2.20m up, inside a
 * 2.4m clear height with only 0.2m margin — too tight given the bore is
 * also only 2.2m WIDE, so the horizontal pull-back at d=2.3 (~1.96m) nearly
 * spans the whole clear width too). A smaller clamp reads correctly in
 * both axes at once.
 */
export const SEWER_PIPE_CAMERA_MAX_DISTANCE = 1.9;
/** Chamber ceiling 3.5m — the brief's own math: caps at ~2.36m camera height at pitch 0.55, comfortably under 3.5m; horizontal pull-back (~2.22m) sits well inside the 12×10m room. */
export const SEWER_CHAMBER_CAMERA_MAX_DISTANCE = 2.6;

/** `blk_sewer_chamber`: 12.0m(X) × 10.0m(Z) interior, ceiling 3.5m — matches the delivered GLB exactly (independent inspection this session ran). */
const CHAMBER_HALF_WIDTH = 6;
const CHAMBER_HALF_DEPTH = 5;
export const SEWER_CHAMBER = {
  minX: SEWER_ORIGIN.x - CHAMBER_HALF_WIDTH,
  maxX: SEWER_ORIGIN.x + CHAMBER_HALF_WIDTH,
  minZ: SEWER_ORIGIN.z - CHAMBER_HALF_DEPTH,
  maxZ: SEWER_ORIGIN.z + CHAMBER_HALF_DEPTH,
  height: 3.5,
};
/** The chamber's own real open south mouth (2.6m wide, matches the pipe's outer profile) — the ONLY gap in the room's walls; the north mouth (grated shut, per the GLB's own baked `shut_grate_h/v` geometry) is honestly closed, not interactable. */
const SOUTH_MOUTH_HALF = 1.3;

/** The basin — a raised-lip standing-water feature along the chamber's own north wall (per the delivered GLB's `basin_lip`/`water_basin` nodes, world/block.ts renders it) — bottom stays ≥y=0 (the GLB's own `water_basin` node sits at local Y 0.12-0.24, nothing recesses below the floor plane). Gray-box dressing only, no collider (short enough — 0.45m tall — to be pure floor-level detail, not an obstacle). */
export const SEWER_BASIN = { x: SEWER_ORIGIN.x, z: SEWER_CHAMBER.maxZ - 0.6, width: 5, depth: 1.55 };

export const ELITE_HOME = { x: SEWER_ORIGIN.x, z: SEWER_ORIGIN.z };

/** Just past the pipe's own north end, inside the chamber — where a player walking the bore first reaches open chamber floor. */
export const SEWER_CHAMBER_SPAWN = { x: SEWER_ORIGIN.x, z: SEWER_CHAMBER.minZ + 1.5 };

/** Four solid chamber walls with the south mouth gap; the pipe run is modeled as two long side-walls (the bore's own outer profile) rather than a fourth room, matching `blk_sewer_pipe`'s own "short connector" framing — a real collision corridor, not a separate teleport-isolated room. */
export function sewerWalls(): BoxCollider[] {
  const t = 0.4;
  const c = SEWER_CHAMBER;
  const walls: BoxCollider[] = [
    { minX: c.minX, maxX: c.maxX, minZ: c.maxZ - t, maxZ: c.maxZ, topY: c.height, bottomY: 0 }, // north (grated — solid, no interaction)
    { minX: c.minX, maxX: c.minX + t, minZ: c.minZ, maxZ: c.maxZ, topY: c.height, bottomY: 0 }, // west
    { minX: c.maxX - t, maxX: c.maxX, minZ: c.minZ, maxZ: c.maxZ, topY: c.height, bottomY: 0 }, // east
    // south — split around the pipe mouth gap
    { minX: c.minX, maxX: SEWER_ORIGIN.x - SOUTH_MOUTH_HALF, minZ: c.minZ - t, maxZ: c.minZ, topY: c.height, bottomY: 0 },
    { minX: SEWER_ORIGIN.x + SOUTH_MOUTH_HALF, maxX: c.maxX, minZ: c.minZ - t, maxZ: c.minZ, topY: c.height, bottomY: 0 },
  ];
  // Pipe bore side walls — extended PIPE_EXIT_STUB further south than the
  // pipe's own rendered mouth (see that constant's comment) so the walk-out
  // threshold below is actually reachable.
  const pipeMinZ = PIPE_ENTRY.z - PIPE_EXIT_STUB;
  const pipeMaxZ = c.minZ;
  walls.push(
    { minX: SEWER_ORIGIN.x - PIPE_HALF_WIDTH, maxX: SEWER_ORIGIN.x - PIPE_CLEAR_HALF, minZ: pipeMinZ, maxZ: pipeMaxZ, topY: 2.6, bottomY: PIPE_WALK_FLOOR_Y - 0.4 },
    { minX: SEWER_ORIGIN.x + PIPE_CLEAR_HALF, maxX: SEWER_ORIGIN.x + PIPE_HALF_WIDTH, minZ: pipeMinZ, maxZ: pipeMaxZ, topY: 2.6, bottomY: PIPE_WALK_FLOOR_Y - 0.4 },
    // South end cap (behind the extended stub) — same "prevent a sideways/backward drift off the end of an isolated corridor" reasoning as every other tunnel/ramp back-wall in this game.
    { minX: SEWER_ORIGIN.x - PIPE_HALF_WIDTH, maxX: SEWER_ORIGIN.x + PIPE_HALF_WIDTH, minZ: pipeMinZ - 0.4, maxZ: pipeMinZ, topY: 3, bottomY: PIPE_WALK_FLOOR_Y - 0.4 },
  );
  return walls;
}

export function sewerFloors(): FloorRegion[] {
  return [
    { kind: 'flat', minX: SEWER_CHAMBER.minX, maxX: SEWER_CHAMBER.maxX, minZ: SEWER_CHAMBER.minZ, maxZ: SEWER_CHAMBER.maxZ, y: 0, priority: 1 },
    { kind: 'flat', minX: SEWER_ORIGIN.x - PIPE_CLEAR_HALF, maxX: SEWER_ORIGIN.x + PIPE_CLEAR_HALF, minZ: PIPE_ENTRY.z - PIPE_EXIT_STUB - 0.5, maxZ: SEWER_CHAMBER.minZ, y: PIPE_WALK_FLOOR_Y, priority: 1 },
  ];
}

// --- Street trap hole: walk-in trigger (T3 door law) ---
const TRAP_HOLE_MOUTH_RADIUS = 1.2; // hole mouth ~1.4m diameter -> ~0.7 radius visually; trigger uses a slightly wider ring so any real approach fires, matching every other radius-based interactable's own generosity
export function isAtTrapHoleFace(x: number, y: number, z: number): boolean {
  return Math.hypot(x - TRAP_HOLE_POS.x, z - TRAP_HOLE_POS.z) <= TRAP_HOLE_MOUTH_RADIUS && y > -1; // street level only, never a false-positive from inside the isolated sewer origin
}

/**
 * Fix session (review finding, corroborated by all 3 lenses): the entry
 * gate's own comment ("reads 'pushing toward the hole's center'") was never
 * actually implemented — the code only checked `sewerDesiredLen > 0.05`, any
 * movement in any direction within the trigger radius, so walking laterally
 * past the hole (or being knocked into it) dropped the player in. This is
 * the real dot-product gate the comment always claimed to be, matching
 * every other directional door-law trigger in this file (isAtTunnelBreachFace
 * et al. use a fixed +Z bias since their doors face one way; the trap hole
 * is a radial, any-approach feature, so it needs the general form: the
 * desired-direction vector must point genuinely toward the hole's center,
 * not just be nonzero).
 */
export function isPushingTowardTrapHole(x: number, z: number, dirX: number, dirZ: number): boolean {
  const toHoleX = TRAP_HOLE_POS.x - x;
  const toHoleZ = TRAP_HOLE_POS.z - z;
  const toHoleLen = Math.hypot(toHoleX, toHoleZ);
  if (toHoleLen < 1e-4) return true; // standing exactly on the hole's own center — any movement counts
  const dirLen = Math.hypot(dirX, dirZ);
  if (dirLen < 1e-4) return false;
  const dot = (toHoleX / toHoleLen) * (dirX / dirLen) + (toHoleZ / toHoleLen) * (dirZ / dirLen);
  return dot > 0.5;
}

/**
 * Walking back south past the pipe's own entry mouth — the walk-out exit.
 * Fix session: this threshold (z < PIPE_ENTRY.z - EXIT_MARGIN = -13.35) was
 * previously UNREACHABLE — the south end-cap sat flush against PIPE_ENTRY.z,
 * and `resolveHorizontal`'s PLAYER_RADIUS expansion capped the deepest
 * reachable z at -12.7. `PIPE_EXIT_STUB` (above) pushes the real wall back
 * far enough that this same threshold is now comfortably inside the walkable
 * band (reachable down to roughly PIPE_ENTRY.z - PIPE_EXIT_STUB - 0.1).
 */
const EXIT_MARGIN = PLAYER_RADIUS + 0.05;
export function isPastPipeEntryThreshold(x: number, z: number): boolean {
  return z < PIPE_ENTRY.z - EXIT_MARGIN && Math.abs(x - SEWER_ORIGIN.x) < PIPE_CLEAR_HALF + EXIT_MARGIN;
}

/**
 * Where exiting the sewer den lands the player — offset south of the trap
 * hole's own trigger radius (mirrors `TUNNEL_BREACH_STANDPOINT`'s "land
 * clear of the entry trigger" pattern). Fix session: the original exit
 * teleported the player to `TRAP_HOLE_POS` itself — dead center of
 * `isAtTrapHoleFace`'s own 1.2m trigger ring — so any held movement on the
 * very next tick re-entered immediately (an exit/enter loop, worse once the
 * entry gate's own directional-intent fix above is in place, since standing
 * exactly ON the hole's center makes every direction "toward center").
 */
export const TRAP_HOLE_STANDPOINT = { x: TRAP_HOLE_POS.x, z: TRAP_HOLE_POS.z - (TRAP_HOLE_MOUTH_RADIUS + PLAYER_RADIUS + 0.5) };

/** True while inside the pipe bore (not yet in the chamber) — `main.ts`'s `activeCameraMaxDistance()` uses this to pick the tighter bore clamp over the chamber's own. */
export function isInSewerPipe(z: number): boolean {
  return z < SEWER_CHAMBER.minZ;
}
